import { Link, useRouteLoaderData } from 'react-router'
import { type SiteSettings } from '#app/components/site-navigation'
import { SiteLogo } from './site-navigation.tsx'

interface GlobalFooterProps {
	siteSettings: SiteSettings | null
}

export function GlobalFooter({ siteSettings }: GlobalFooterProps) {
	const rootData = useRouteLoaderData('root')

	if (!siteSettings) return null
	const currentYear = new Date().getFullYear()
	const copyrightText =
		siteSettings.footerCopyright?.replace(/\d{4}/, currentYear.toString()) ||
		`Copyright ${currentYear} | Zeta Display`

	return (
		<footer className="bg-card-gradient text-accent-foreground container my-10 rounded-4xl">
			<div className="container p-12">
				<div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
					{/* Logo and Description Section */}
					<div className="space-y-6 lg:col-span-5">
						<div className="flex items-center">
							<SiteLogo siteSettings={siteSettings} className="" />
							<h1 className="text-h1 ml-8">the Box</h1>
						</div>

						{siteSettings.footerDescription && (
							<p className="text-body-md text-accent-foreground/80 max-w-md leading-relaxed">
								{siteSettings.footerDescription}
							</p>
						)}
					</div>

					{/* Menu Columns Section */}
					{siteSettings.footerMenuColumns &&
						siteSettings.footerMenuColumns.length > 0 && (
							<div className="lg:col-span-7">
								<div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
									{siteSettings.footerMenuColumns.map((column, index) => (
										<div key={index} className="space-y-4">
											<h3 className="text-body-md text-accent-foreground font-semibold tracking-wide uppercase">
												{column.columnTitle}
											</h3>
											<ul className="space-y-3">
												{column.menuItems?.map((item, itemIndex) => (
													<li key={itemIndex}>
														<Link
															to={item.url ?? '#'}
															target={item.openInNewTab ? '_blank' : undefined}
															rel={
																item.openInNewTab
																	? 'noopener noreferrer'
																	: undefined
															}
															className="text-body-md text-accent-foreground/70 hover:text-accent-foreground transition-colors duration-200 hover:underline hover:underline-offset-6"
														>
															{item.title}
														</Link>
													</li>
												))}
											</ul>
										</div>
									))}
								</div>
							</div>
						)}
				</div>

				{/* Copyright Section */}
				<div className="mt-12 pt-8">
					<div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
						<p className="text-body-sm text-accent-foreground/60">
							{copyrightText}
						</p>

						{/* Zeta Logo - Bottom Right */}
						{rootData?.siteSettings?.zetaLogo && (
							<div className="flex items-center">
								<div
									className="w-24 opacity-80 transition-opacity hover:opacity-100 text-accent-foreground"
									dangerouslySetInnerHTML={{ __html: rootData.siteSettings.zetaLogo }}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</footer>
	)
}
